import { usePairs } from '@/store/pairs';
import {
    Bar,
    DatafeedConfiguration,
    IBasicDataFeed,
    OnReadyCallback,
    ResolutionString,
    SearchSymbolResultItem,
} from '../../../public/charting_library';
import { getHistoricalData as aevoHistoricalData } from './historycal/aevo';
import { getHistoricalData as bybitGetHistoricalData } from './historycal/bybit';
import { getHistoricalData } from './historycal/historycall';
import { getHistoricalDataHoldStation as forexGetHistoricalData } from './historycal/holdstation';
import { getHistoricalDataOkx } from './historycal/okx';
import { subscribeOnStream, unsubscribeFromStream } from './streaming';

const mapResolution: any = {
    '1': 60000,
    '5': 300000,
    '15': 900000,
    '60': 3600000,
    '240': 14400000,
    '1440': 86400000,
};

const mapSymbol: any = {
    ZKUSD: 'ZK-PERP',
};

const lastBarsCache = new Map<string, Bar>();
const configurationData: DatafeedConfiguration = {
    supported_resolutions: [
        '1',
        '5',
        '15',
        '1h',
        '4h',
        '24h',
    ] as ResolutionString[],
    exchanges: [
        {
            /** Value to be passed as the `exchange` argument to `searchSymbols` */
            value: 'BINANCE',
            /** Name of the exchange */
            name: 'Binance',
            /** Description of the exchange */
            desc: 'Binance SPOT',
        },
    ],
    symbols_types: [
        {
            name: 'crypto',

            // `symbolType` argument for the `searchSymbols` method, if a user selects this symbol type
            value: 'crypto',
        },
        {
            name: 'forex',
            value: 'forex',
        },
        {
            name: 'Indices',
            value: 'indices',
        },
    ],
};

const mapper: any = {
    '1': '1m',
    '5': '5m',
    '15': '15m',
    '60': '1h',
    '240': '4h',
    '1440': '1d',
};

const localHistoryData: Record<string, Bar[]> = {};

export const datafeed: IBasicDataFeed = {
    onReady: (callback: OnReadyCallback) => {
        // console.log("[onReady]: Method call");
        setTimeout(() => callback(configurationData));
    },
    resolveSymbol: async (
        symbolName,
        onSymbolResolvedCallback,
        onResolveErrorCallback
    ) => {
        // console.log('[resolveSymbol]: Method call', symbolName);
        const symbolInfo = usePairs
            .getState()
            .symbols.find((o) => o.ticker === symbolName);

        if (!symbolInfo) {
            console.log('[resolveSymbol]: Cannot resolve symbol', symbolName);
            onResolveErrorCallback('cannot resolve symbol');
            return;
        }
        onSymbolResolvedCallback(symbolInfo);
    },
    getBars: async (
        symbolInfo,
        resolution,
        periodParams,
        onHistoryCallback,
        onErrorCallback
    ) => {
        const { firstDataRequest, countBack } = periodParams;

        // useSwapBoxStore.getState().setResolution(resolution);
        try {
            if (
                symbolInfo.listed_exchange.toLowerCase() === 'binance' ||
                symbolInfo.listed_exchange.toLowerCase() === 'binance_future'
            ) {
                const requestParasm = {
                    symbol: symbolInfo.ticker || '',
                    interval: mapper[resolution] || '1',
                    limit: 1000,
                    from:
                        periodParams.to * 1000 -
                        1000 * mapResolution[resolution],
                    to: periodParams.to * 1000,
                };

                const isfuture =
                    symbolInfo.listed_exchange.toLowerCase() ===
                    'binance_future';

                const currentBars = await getHistoricalData(
                    isfuture,
                    requestParasm
                );

                if (currentBars.length === 0) {
                    return onHistoryCallback([], {
                        noData: true,
                    });
                }

                const bars = currentBars.slice(
                    Math.max(currentBars.length - countBack, 0)
                );

                if (firstDataRequest) {
                    if (
                        bars.length < periodParams.countBack &&
                        periodParams.countBack > 1000
                    ) {
                        return onHistoryCallback(
                            localHistoryData[
                                `${symbolInfo.ticker || ''}-${resolution}`
                            ],
                            {
                                noData: false,
                            }
                        );
                    }
                    lastBarsCache.set(
                        `${symbolInfo.ticker || ''}-${resolution}`,
                        {
                            ...bars[bars.length - 1],
                        }
                    );
                    localHistoryData[
                        `${symbolInfo.ticker || ''}-${resolution}`
                    ] = bars;
                    return onHistoryCallback(bars, {
                        noData: false,
                    });
                } else {
                    const current =
                        localHistoryData[
                            `${symbolInfo.ticker || ''}-${resolution}`
                        ];

                    if (
                        current[0].time - bars[bars.length - 1].time >
                        mapResolution[resolution]
                    ) {
                        return onHistoryCallback([], {
                            noData: true,
                        });
                    }

                    const newBars = [...bars, ...current];
                    localHistoryData[
                        `${symbolInfo.ticker || ''}-${resolution}`
                    ] = newBars;

                    if (bars.length < periodParams.countBack) {
                        return onHistoryCallback(newBars);
                    }

                    return onHistoryCallback(newBars, {
                        noData: false,
                    });
                }
            }

            if (symbolInfo.listed_exchange.toLowerCase() === 'okx') {
                const interval =
                    Number(resolution) > 15
                        ? mapper[resolution]?.toUpperCase()
                        : mapper[resolution];
                const requestParasm = {
                    symbol: symbolInfo.ticker || '',
                    interval: interval || '1',
                    limit: 1000,
                    from:
                        periodParams.to * 1000 -
                        1000 * mapResolution[resolution],
                    to: periodParams.to * 1000,
                };

                const currentBars = await getHistoricalDataOkx(requestParasm);

                if (currentBars.length === 0) {
                    return onHistoryCallback([], {
                        noData: true,
                    });
                }

                const bars = currentBars.slice(
                    Math.max(currentBars.length - countBack, 0)
                );

                if (firstDataRequest) {
                    if (
                        bars.length < periodParams.countBack &&
                        periodParams.countBack > 1000
                    ) {
                        return onHistoryCallback(
                            localHistoryData[
                                `${symbolInfo.ticker || ''}-${resolution}`
                            ],
                            {
                                noData: false,
                            }
                        );
                    }
                    lastBarsCache.set(
                        `${symbolInfo.ticker || ''}-${resolution}`,
                        {
                            ...bars[bars.length - 1],
                        }
                    );
                    localHistoryData[
                        `${symbolInfo.ticker || ''}-${resolution}`
                    ] = bars;
                    return onHistoryCallback(bars, {
                        noData: false,
                    });
                } else {
                    const current =
                        localHistoryData[
                            `${symbolInfo.ticker || ''}-${resolution}`
                        ];

                    if (
                        current[0].time - bars[bars.length - 1].time >
                        mapResolution[resolution]
                    ) {
                        return onHistoryCallback([], {
                            noData: true,
                        });
                    }

                    const newBars = [...bars, ...current];
                    localHistoryData[
                        `${symbolInfo.ticker || ''}-${resolution}`
                    ] = newBars;

                    if (bars.length < periodParams.countBack) {
                        return onHistoryCallback(newBars);
                    }

                    return onHistoryCallback(newBars, {
                        noData: false,
                    });
                }
            }

            if (symbolInfo.listed_exchange.toLowerCase() === 'bybit') {
                let bars;

                try {
                    const currentBars = await bybitGetHistoricalData({
                        symbol: symbolInfo.ticker || '',
                        interval: mapper[resolution] || '1',
                        limit: 1000,
                        from:
                            periodParams.to * 1000 -
                            1000 * mapResolution[resolution],
                        to: periodParams.to * 1000,
                    });

                    bars = currentBars.slice(
                        Math.max(currentBars.length - countBack, 0)
                    );

                    if (bars.length === 0) {
                        return onHistoryCallback([], { noData: true });
                    }
                } catch (error) {
                    console.error('Error get bars forex', error);
                    return onHistoryCallback([], { noData: true });
                }

                if (firstDataRequest) {
                    if (
                        bars.length < periodParams.countBack &&
                        periodParams.countBack > 1000
                    ) {
                        return onHistoryCallback(
                            localHistoryData[
                                `${symbolInfo.ticker || ''}-${resolution}`
                            ],
                            {
                                noData: false,
                            }
                        );
                    }

                    lastBarsCache.set(
                        `${symbolInfo.ticker || ''}-${resolution}`,
                        {
                            ...bars[bars.length - 1],
                        }
                    );

                    localHistoryData[
                        `${symbolInfo.ticker || ''}-${resolution}`
                    ] = bars;
                    return onHistoryCallback(bars, { noData: false });
                } else {
                    const current =
                        localHistoryData[
                            `${symbolInfo.ticker || ''}-${resolution}`
                        ];

                    if (
                        current[0].time - bars[bars.length - 1].time >
                        mapResolution[resolution]
                    ) {
                        return onHistoryCallback([], {
                            noData: true,
                        });
                    }

                    const newBars = [...bars, ...current];
                    localHistoryData[
                        `${symbolInfo.ticker || ''}-${resolution}`
                    ] = newBars;
                    return onHistoryCallback(newBars, {
                        noData: newBars.length < periodParams.countBack,
                    });
                }
            }

            if (symbolInfo.listed_exchange.toLowerCase() === 'holdstation') {
                const requestParasm = {
                    symbol: mapSymbol[symbolInfo.ticker ?? ''] || '',
                    interval: mapper[resolution] || '1',
                    from:
                        periodParams.to * 1000 -
                        1000 * mapResolution[resolution],
                    to: periodParams.to * 1000,
                };

                const currentBars = await aevoHistoricalData(requestParasm);

                if (currentBars.length === 0) {
                    return onHistoryCallback([], {
                        noData: true,
                    });
                }

                const bars = currentBars.slice(
                    Math.max(currentBars.length - countBack, 0)
                );

                if (firstDataRequest) {
                    if (
                        bars.length < periodParams.countBack &&
                        periodParams.countBack > 1000
                    ) {
                        return onHistoryCallback(
                            localHistoryData[
                                `${symbolInfo.ticker || ''}-${resolution}`
                            ],
                            {
                                noData: false,
                            }
                        );
                    }
                    lastBarsCache.set(
                        `${symbolInfo.ticker || ''}-${resolution}`,
                        {
                            ...bars[bars.length - 1],
                        }
                    );
                    localHistoryData[
                        `${symbolInfo.ticker || ''}-${resolution}`
                    ] = bars;
                    return onHistoryCallback(bars, {
                        noData: false,
                    });
                } else {
                    const current =
                        localHistoryData[
                            `${symbolInfo.ticker || ''}-${resolution}`
                        ];

                    if (
                        current[0].time - bars[bars.length - 1].time >
                        mapResolution[resolution]
                    ) {
                        return onHistoryCallback([], {
                            noData: true,
                        });
                    }

                    const newBars = [...bars, ...current];
                    localHistoryData[
                        `${symbolInfo.ticker || ''}-${resolution}`
                    ] = newBars;

                    if (bars.length < periodParams.countBack) {
                        return onHistoryCallback(newBars);
                    }

                    return onHistoryCallback(newBars, {
                        noData: false,
                    });
                }
            }

            if (symbolInfo?.exchange?.toLowerCase() === 'forex') {
                let bars;

                try {
                    const currentBars = await forexGetHistoricalData({
                        symbol: symbolInfo.ticker || '',
                        interval: mapper[resolution] || '1',
                        limit: 1000,
                        from:
                            periodParams.to * 1000 -
                            1000 * mapResolution[resolution],
                        to: periodParams.to * 1000,
                    });
                    bars = currentBars.slice(
                        Math.max(currentBars.length - countBack, 0)
                    );
                } catch (error) {
                    console.error('Error get bars forex', error);
                    return onHistoryCallback([], { noData: true });
                }

                if (firstDataRequest) {
                    if (
                        bars.length < periodParams.countBack &&
                        periodParams.countBack > 1000
                    ) {
                        return onHistoryCallback(
                            localHistoryData[
                                `${symbolInfo.ticker || ''}-${resolution}`
                            ],
                            {
                                noData: false,
                            }
                        );
                    }

                    lastBarsCache.set(
                        `${symbolInfo.ticker || ''}-${resolution}`,
                        {
                            ...bars[bars.length - 1],
                        }
                    );

                    localHistoryData[
                        `${symbolInfo.ticker || ''}-${resolution}`
                    ] = bars;
                    return onHistoryCallback(bars, { noData: false });
                } else {
                    const current =
                        localHistoryData[
                            `${symbolInfo.ticker || ''}-${resolution}`
                        ];

                    if (
                        current[0].time - bars[bars.length - 1].time >
                        mapResolution[resolution]
                    ) {
                        return onHistoryCallback([], {
                            noData: true,
                        });
                    }

                    const newBars = [...bars, ...current];
                    localHistoryData[
                        `${symbolInfo.ticker || ''}-${resolution}`
                    ] = newBars;
                    return onHistoryCallback(newBars, {
                        noData: newBars.length < periodParams.countBack,
                    });
                }
            }

            if (symbolInfo.listed_exchange.toLowerCase() === 'stock') {
                let bars;

                try {
                    const currentBars = await forexGetHistoricalData({
                        symbol: symbolInfo.ticker || '',
                        interval: mapper[resolution] || '1',
                        limit: 1000,
                        from:
                            periodParams.to * 1000 -
                            1000 * mapResolution[resolution],
                        to: periodParams.to * 1000,
                    });
                    bars = currentBars.slice(
                        Math.max(currentBars.length - countBack, 0)
                    );
                } catch (error) {
                    console.error('Error get bars forex', error);
                    return onHistoryCallback([], { noData: true });
                }

                if (firstDataRequest) {
                    if (
                        bars.length < periodParams.countBack &&
                        periodParams.countBack > 1000
                    ) {
                        return onHistoryCallback(
                            localHistoryData[
                                `${symbolInfo.ticker || ''}-${resolution}`
                            ],
                            {
                                noData: false,
                            }
                        );
                    }

                    lastBarsCache.set(
                        `${symbolInfo.ticker || ''}-${resolution}`,
                        {
                            ...bars[bars.length - 1],
                        }
                    );

                    localHistoryData[
                        `${symbolInfo.ticker || ''}-${resolution}`
                    ] = bars;
                    return onHistoryCallback(bars, { noData: false });
                } else {
                    const current =
                        localHistoryData[
                            `${symbolInfo.ticker || ''}-${resolution}`
                        ];

                    if (
                        current[0].time - bars[bars.length - 1].time >
                        mapResolution[resolution]
                    ) {
                        return onHistoryCallback([], {
                            noData: true,
                        });
                    }

                    const newBars = [...bars, ...current];
                    localHistoryData[
                        `${symbolInfo.ticker || ''}-${resolution}`
                    ] = newBars;
                    return onHistoryCallback(newBars, {
                        noData: newBars.length < periodParams.countBack,
                    });
                }
            }

            throw new Error(
                `unknown exchange: ${symbolInfo.listed_exchange.toLowerCase()}`
            );
        } catch (error: any) {
            console.log('[getBars]: Get error', error);
            onErrorCallback(error);
        }
    },
    searchSymbols: async (
        userInput,
        exchange,
        symbolType,
        onResultReadyCallback
    ) => {
        const newSymbols = usePairs
            .getState()
            .symbols.filter((symbol) => {
                const isExchangeValid =
                    exchange === '' || symbol.exchange === exchange;
                const isFullSymbolContainsInput =
                    symbol.full_name
                        .toLowerCase()
                        .indexOf(userInput.toLowerCase()) !== -1;
                return isExchangeValid && isFullSymbolContainsInput;
            })
            .map((o) => {
                const ele: SearchSymbolResultItem = {
                    symbol: o.name,
                    full_name: o.full_name,
                    description: o.description,
                    exchange: o.exchange,
                    ticker: o.ticker,
                    /**
                     * Type of symbol
                     *
                     * 'stock' | 'futures' | 'forex' | 'index'
                     */
                    type: o.type,
                };

                return ele;
            });

        onResultReadyCallback(newSymbols);
    },
    subscribeBars: (
        symbolInfo,
        resolution,
        onRealtimeCallback,
        subscriberUID,
        onResetCacheNeededCallback
    ) => {
        console.log(
            '[subscribeBars]: Method call with subscriberUID:',
            subscriberUID,
            resolution
        );
        subscribeOnStream(
            symbolInfo,
            resolution,
            onRealtimeCallback,
            subscriberUID,
            onResetCacheNeededCallback,
            lastBarsCache.get(`${symbolInfo.ticker || ''}-${resolution}`)
        );
    },

    unsubscribeBars: (subscriberUID) => {
        console.log(
            '[unsubscribeBars]: Method call with subscriberUID:',
            subscriberUID
        );
        unsubscribeFromStream(subscriberUID);
    },
};
